import client from '@atom/graph/client';
import configuration from '@atom/utilities/configuration';
import { redirectDomain, subdomain } from '@atom/utilities/endpoints';
import fullStoryUtilities from '@atom/utilities/fullstoryUtilities';

import { configureStore } from './store/configureStore';
import Root from './Root';
import rootSaga from './sagas';

import './styles/colors.css';
import './styles/fonts.css';
import './styles/layout.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './styles/override/calendar.css';
import './styles/iconfont/material-icons.css';

// Skip FullStory on the demo deploy — VITE_DEMO_MODE builds are production
// (MODE=production) but shouldn't record sessions into the real org.
if (
  configuration.environment === 'production' &&
  import.meta.env.VITE_DEMO_MODE !== 'true'
) {
  fullStoryUtilities.instantiateFullStory();
}

const store = configureStore();

store.runSaga(rootSaga);

// If a user lands on the subdomainless page, they will be redirected
// to the www domain.
if (!subdomain) {
  window.location.replace(redirectDomain);
}

if (configuration.env === configuration.constants.LOCAL) {
  document.title = `LOCAL - ${document.title}`;
} else if (configuration.env === configuration.constants.DEV) {
  document.title = `DEVELOPMENT - ${document.title}`;
} else if (configuration.env === configuration.constants.QA) {
  document.title = `QA - ${document.title}`;
} else if (configuration.env === configuration.constants.UAT) {
  document.title = `UAT - ${document.title}`;
}

const bootstrap = async () => {
  if (configuration.env === configuration.constants.LOCAL) {
    const { worker } = await import('./mocks/browser');
    await worker.start({
      onUnhandledRequest: 'bypass',
      serviceWorker: { url: '/mockServiceWorker.js' },
    });
    // LOCAL bypasses the getLoggedInUser saga that normally sets the role.
    // Without this, the topbar's CreateControls + a handful of other
    // permission-gated UI bits silently render as hidden because
    // `currentUserRole` stays null. Set it explicitly to Admin so the
    // demo user has full permissions.
    const auth = await import('./utilities/authUtilities');
    auth.setUserRole(auth.ROLES.ADMIN_ROLE ?? 'Admin');
    auth.setUserRoleLevel(0);

    // WorkOrderContext.getUserIdSync calls jwtDecode(cookie) — inject a fake
    // but structurally valid JWT so it doesn't throw in LOCAL (no real auth).
    const { persistAccountPayload } = await import('./utilities/accountUtilities');
    const jwtHeader = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    const jwtPayload = btoa(JSON.stringify({
      email: 'tyler.pauley@atom-ai.com',
      sub: 'local-demo-user',
      organizations: null,
    })).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    await persistAccountPayload(`${jwtHeader}.${jwtPayload}.local_fake_sig`);
  }
  Root.initializeApplication(store, client);

  // Demo Controls overlay (PM threshold demo, mock-clock tick, sensor push).
  // LOCAL only.
  if (configuration.env === configuration.constants.LOCAL) {
    const [{ createRoot }, { default: React }, { default: DemoControls }] =
      await Promise.all([
        import('react-dom/client'),
        import('react'),
        import('./components/demoControls/DemoControls'),
      ]);
    const host = document.createElement('div');
    host.id = 'demo-controls-root';
    document.body.appendChild(host);
    createRoot(host).render(React.createElement(DemoControls));
  }
};

bootstrap();
